<template>
  <div class="project-schedule">
    <div v-if="loading" class="schedule-loading">Loading schedule…</div>
    <div v-else-if="error" class="schedule-error">{{ error }}</div>
    <div v-else class="schedule-content">
      <section class="schedule-pressure">
        <h2>Schedule Pressure</h2>
        <dl>
          <dt>Overdue</dt><dd>{{ data.pressure.overdue }}</dd>
          <dt>Due this week</dt><dd>{{ data.pressure.due_this_week }}</dd>
          <dt>Due next week</dt><dd>{{ data.pressure.due_next_week }}</dd>
          <dt>Due this month</dt><dd>{{ data.pressure.due_this_month }}</dd>
        </dl>
      </section>

      <section v-if="data.overdue.length" class="schedule-overdue">
        <h2>Overdue Milestones</h2>
        <ul>
          <li v-for="ms in data.overdue" :key="ms.milestoneId" class="overdue">
            <strong>{{ ms.title }}</strong> — {{ ms.projectName }}
            <span v-if="ms.dueOn"> (was due {{ ms.dueOn }})</span>
          </li>
        </ul>
      </section>

      <section v-if="data.upcoming.length" class="schedule-upcoming">
        <h2>Upcoming (14 days)</h2>
        <ul>
          <li v-for="ms in data.upcoming" :key="ms.milestoneId">
            <strong>{{ ms.title }}</strong> — {{ ms.projectName }}
            <span v-if="ms.dueOn"> (due {{ ms.dueOn }})</span>
          </li>
        </ul>
      </section>

      <section v-if="data.conflicts.length" class="schedule-conflicts">
        <h2>Due-Date Conflicts</h2>
        <div v-for="conflict in data.conflicts" :key="conflict.date" class="conflict-group">
          <h3>{{ conflict.date }} ({{ conflict.count }} milestones)</h3>
          <ul>
            <li v-for="ms in conflict.milestones" :key="ms.milestoneId">
              {{ ms.title }} — {{ ms.projectName }}
            </li>
          </ul>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'

const props = defineProps<{ url: string }>()

interface Milestone {
  milestoneId: number
  title: string
  status: string
  projectName: string
  projectId: number
  customer: string | null
  dueOn: string | null
}

interface Conflict {
  date: string
  count: number
  milestones: Milestone[]
}

interface Pressure {
  overdue: number
  due_this_week: number
  due_next_week: number
  due_this_month: number
}

interface ScheduleData {
  pressure: Pressure
  upcoming: Milestone[]
  overdue: Milestone[]
  conflicts: Conflict[]
}

const data = ref<ScheduleData>({
  pressure: { overdue: 0, due_this_week: 0, due_next_week: 0, due_this_month: 0 },
  upcoming: [],
  overdue: [],
  conflicts: []
})
const loading = ref(true)
const error = ref<string | null>(null)

onMounted(async () => {
  try {
    const response = await fetch(props.url, {
      headers: { 'Accept': 'application/json' },
      credentials: 'same-origin'
    })
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    data.value = await response.json()
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Failed to load schedule'
  } finally {
    loading.value = false
  }
})
</script>
